// useAuthenticatorApp — the ONE public seam for the user's authenticator-app (TOTP) sign-in factor
// (`/auth/mfa/totp/*`, ORP-28354 backend, ORP-28358 here). It exists for the same reason as
// `useChangePassword`: contract 06 §4 keeps `AuthApi`/`AuthService` feature-internal, so the
// settings pane that hosts the controls calls this instead. Deliberately flow-less — each call is
// one request plus the typed error, with no store write and no navigation to orchestrate.
//
// The authenticator is an ALTERNATIVE to the emailed sign-in code, never a replacement: nothing
// here touches the account-level MFA switch, and the mailed code keeps working.
//
// `secret` / `otpauth_uri` are credentials. They live only in this instance's `setup` ref — never a
// Pinia store, a URL, a log line or an analytics event — and are dropped the moment the setup ends
// (confirmed, cancelled, expired, or the owning scope disposed).
import { getCurrentScope, onScopeDispose, ref, type Ref } from 'vue'

import { AuthError } from '../models/auth.error'
import type { TotpSetup, TotpStatus } from '../models/auth.types'
import { authService } from '../service/AuthService'

export type TotpFailureKind =
  /** `TOTP_INVALID_CODE` — the app's six digits are wrong. */
  | 'invalidCode'
  /** `TOTP_EMAIL_CODE_INVALID` — the mailed six digits are wrong. */
  | 'invalidEmailCode'
  /** `INVALID_OLD_PASSWORD` — the current password is wrong. */
  | 'invalidPassword'
  /** A 422 that named an input (`fields.code|password|email_code`): empty or malformed. */
  | 'invalidField'
  /** `TOTP_SETUP_REQUIRED` — the pending setup (or its mailed code) is gone; start again. */
  | 'setupRequired'
  /** `TOTP_ALREADY_ENABLED` — already on; the status is re-read, the setup dropped. */
  | 'alreadyEnabled'
  /** `MFA_TOO_MANY_CODES` — the per-user code budget; `retryAfterSeconds` when named. */
  | 'tooManyCodes'
  /** Everything else (delivery failure, rate limit, transport): render `i18nKey`. */
  | 'other'

export type TotpFailureField = 'code' | 'password' | 'emailCode'

export interface TotpFailure {
  kind: TotpFailureKind
  /** Which input an `invalidField` belongs to; `null` otherwise. */
  field: TotpFailureField | null
  /** The error's own i18n key — an `errors.api.<CODE>` / `errors.http.<kind>` entry. */
  i18nKey: string
  /** `TOTP_EMAIL_CODE_INVALID` only: attempts the mailed code has left, when the server said. */
  attemptsRemaining: number | null
  /** `MFA_TOO_MANY_CODES` only: whole seconds until a code can be requested again, when named. */
  retryAfterSeconds: number | null
}

export type AuthenticatorLoadState = 'loading' | 'ready' | 'error'

export interface TotpConfirmInput {
  /** The six digits the authenticator app shows. A STRING — "012345" must survive. */
  code: string
  /** Read when the setup answered `confirm_with: 'password'`. */
  password?: string
  /** Read when the setup answered `confirm_with: 'email_code'`; the six mailed digits. */
  emailCode?: string
}

export interface UseAuthenticatorApp {
  status: Ref<TotpStatus | null>
  loadState: Ref<AuthenticatorLoadState>
  /** The pending setup (secret + QR source), or `null` when none is in progress. */
  setup: Ref<TotpSetup | null>
  pending: Ref<boolean>
  /** The last failure of `startSetup` / `confirm` / `disable`; cleared on the next attempt. */
  failure: Ref<TotpFailure | null>
  load: () => Promise<void>
  /** Resolves `true` when a fresh setup landed in `setup`. */
  startSetup: () => Promise<boolean>
  /** Resolves `true` when the authenticator app is now on. */
  confirm: (input: TotpConfirmInput) => Promise<boolean>
  /** Drops the pending setup locally. The server keeps nothing worth cancelling: a later setup
   *  replaces it, and an unconfirmed device never takes a code at sign-in. */
  cancelSetup: () => void
  /** Resolves `true` when the authenticator app is now off. */
  disable: () => Promise<boolean>
}

const FIELD_BY_WIRE_KEY: Record<string, TotpFailureField> = {
  code: 'code',
  password: 'password',
  email_code: 'emailCode',
}

function naturalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.ceil(value) : null
}

function toFailure(cause: unknown): TotpFailure {
  const error = AuthError.from(cause)
  const base = {
    field: null,
    i18nKey: error.i18nKey,
    attemptsRemaining: null,
    retryAfterSeconds: null,
  } as const

  switch (error.code) {
    case 'totpInvalidCode':
      return { ...base, kind: 'invalidCode', field: 'code' }
    case 'totpEmailCodeInvalid':
      return {
        ...base,
        kind: 'invalidEmailCode',
        field: 'emailCode',
        attemptsRemaining: naturalNumber(error.details.attempts_remaining),
      }
    case 'invalidOldPassword':
      return { ...base, kind: 'invalidPassword', field: 'password' }
    case 'totpSetupRequired':
      return { ...base, kind: 'setupRequired' }
    case 'totpAlreadyEnabled':
      return { ...base, kind: 'alreadyEnabled' }
    case 'mfaTooManyCodes':
      return {
        ...base,
        kind: 'tooManyCodes',
        retryAfterSeconds: naturalNumber(error.details.retry_after),
      }
    default: {
      // `AuthError` reads a lone `fields.password` as a weak NEW password (its signup fallback).
      // On this wire the same field means "empty / malformed current password", so the field name
      // — not the code it inferred — decides.
      const named = Object.keys(error.fields).find((key) => key in FIELD_BY_WIRE_KEY)
      if (named) {
        return {
          ...base,
          kind: 'invalidField',
          field: FIELD_BY_WIRE_KEY[named] ?? null,
          i18nKey: 'errors.http.validation',
        }
      }
      return { ...base, kind: 'other' }
    }
  }
}

export function useAuthenticatorApp(): UseAuthenticatorApp {
  const status = ref<TotpStatus | null>(null)
  const loadState = ref<AuthenticatorLoadState>('loading')
  const setup = ref<TotpSetup | null>(null)
  const pending = ref(false)
  const failure = ref<TotpFailure | null>(null)

  if (getCurrentScope()) onScopeDispose(() => (setup.value = null))

  async function refreshStatus(): Promise<void> {
    status.value = await authService.getTotpStatus()
  }

  async function load(): Promise<void> {
    loadState.value = 'loading'
    try {
      await refreshStatus()
      loadState.value = 'ready'
    } catch {
      loadState.value = 'error'
    }
  }

  /** Runs one write: `pending` + `failure` bookkeeping in one place. */
  async function write(request: () => Promise<boolean>): Promise<boolean> {
    if (pending.value) return false
    pending.value = true
    failure.value = null
    try {
      return await request()
    } catch (cause) {
      failure.value = toFailure(cause)
      if (failure.value.kind === 'setupRequired') setup.value = null
      if (failure.value.kind === 'alreadyEnabled') {
        setup.value = null
        // The server just said "on"; a failed re-read must not contradict it.
        await refreshStatus().catch(() => {
          status.value = { enabled: true, confirmedAt: status.value?.confirmedAt ?? null }
        })
      }
      return false
    } finally {
      pending.value = false
    }
  }

  const startSetup = () =>
    write(async () => {
      setup.value = null // a failed restart must not leave the previous secret on screen
      setup.value = await authService.startTotpSetup()
      return true
    })

  const confirm = (input: TotpConfirmInput) =>
    write(async () => {
      const pendingSetup = setup.value
      if (!pendingSetup) return false
      status.value = await authService.confirmTotp(
        pendingSetup.confirmWith === 'password'
          ? { code: input.code, password: input.password ?? '' }
          : { code: input.code, email_code: input.emailCode ?? '' },
      )
      setup.value = null
      return true
    })

  const disable = () =>
    write(async () => {
      status.value = await authService.disableTotp()
      return true
    })

  function cancelSetup(): void {
    setup.value = null
    failure.value = null
  }

  return {
    status,
    loadState,
    setup,
    pending,
    failure,
    load,
    startSetup,
    confirm,
    cancelSetup,
    disable,
  }
}
